import type { Condition, ListBackupVaultResponse } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useSWRConfig } from 'swr';

import { useDAL } from '@/contexts/useDAL';
import { isVaultsListKey } from '@/data/dal/vaults';
import { VaultColors } from '@/theme/colorSchemes';
import { stringToHash } from '@/utils/string';

import type { BackupVault } from './backupVault';

import { PreferencesKey } from '../dal/preferencesKey';

function useBackupVaults(options?: {
  condition?: Condition;
  pageIndex?: number;
  pageSize?: number;
  enabled?: boolean;
  refreshInterval?: (latestData: ListBackupVaultResponse | undefined) => number;
}): {
  vaults: BackupVault[];
  totalCount: number | undefined;
  isLoading: boolean;
  refresh: () => void;
} {
  const dal = useDAL();
  const { mutate: mutateCache } = useSWRConfig();
  const enabled = options?.enabled !== false;
  // SWR reads a function interval only when its polling effect runs, which happens before the first response, so
  // the interval is derived from the latest data here and passed as a number that restarts polling when it changes.
  const [pollMs, setPollMs] = useState(0);
  const {
    isLoading: vaultsLoading,
    data: vaultsData,
    mutate,
  } = dal.vaults.useList(
    options,
    options?.refreshInterval ? { refreshInterval: pollMs } : undefined
  );
  const getRefreshInterval = options?.refreshInterval;
  useEffect(() => {
    setPollMs(getRefreshInterval ? getRefreshInterval(vaultsData) : 0);
  }, [getRefreshInterval, vaultsData]);

  const {
    data: pref,
    isLoading: prefLoading,
    isValidating: prefValidating,
    mutate: mutatePref,
  } = dal.preferences.useAccountPref(PreferencesKey.BackupVault, enabled);

  // Callers of this hook pass a condition, so its key is not the unfiltered one the tag filter
  // options read; both have to revalidate or a tag saved here stays missing from the menu.
  const refresh = () =>
    void Promise.all([mutate(), mutatePref(), mutateCache(isVaultsListKey)]);

  const vaults = useMemo(
    () =>
      (vaultsData?.vaults || []).map((v) => {
        const userSetting = pref?.value?.colors?.find((p) => p.id === v.id);

        return {
          ...v,
          backgroundColor: userSetting?.color || getDeterministicColor(v.id),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pref?.value?.colors, vaultsData?.vaults, prefValidating]
  );

  return {
    vaults,
    totalCount: vaultsData?.totalCount,
    isLoading: vaultsLoading || prefLoading,
    refresh,
  };
}

export { useBackupVaults };

export function getDeterministicColor(id = 'yo'): string {
  return Object.values(VaultColors)[
    stringToHash(id, Object.values(VaultColors).length)
  ];
}
